import { FormattedMessage } from "@metsooutotec/modes-react-components"
import { Route, Navigate, createBrowserRouter, createRoutesFromElements } from "react-router-dom"
import SiteView from "./features/organizations/sites/pages/site-view"
import CustomerSidebarView from "./features/organizations/customers/components/customer-sidebar-view"
import AssetView from "./features/organizations/assets/pages/asset-view"
import SensorBatchListView from "./features/iot/sensor-batch/pages/sensor-batch-list-view"
import SensorBatchView from "./features/iot/sensor-batch/pages/sensor-batch-view"
import SensorBatchLoader from "./features/iot/sensor-batch/pages/sensor-batch-loader"
import MainLayout from "layouts/MainLayout"
import UsersLoader from "features/users/pages/users-list/users-page-loader"
import UsersPage from "features/users/pages/users-list/users-page"
import UserLoader from "features/users/pages/user/user-page-loader"
import UserPage from "features/users/pages/user/user-page"
import AssetSectionView from "features/organizations/asset-sections/pages/view/asset-section-view"
import AssetSectionEditor from "features/organizations/asset-sections/pages/editor/asset-section-editor"
import LinerMaterialLibraryLoader from "features/provisioning/liner-material-library/liner-material-library-loader"
import LinerMaterialLibraryListView from "features/provisioning/liner-material-library/liner-material-library-list-view"
import ProvisionEventsCustomerSidebarView, {
    customerRouteDataLoader,
} from "features/provisioning/provisions/provision-events/view/provision-events-customer-sidebar-view"
import ProvisionEventsLoader, { provisionEventsRouteDataLoader } from "features/provisioning/provisions/provision-events/provision-events-loader"
import ProvisionEventsListView from "features/provisioning/provisions/provision-events/view/provision-events-list-view"
import ProvisionEventsEquipmentLoader, {
    provisionEventEquipmentDataLoader,
} from "features/provisioning/provisions/provision-events/provision-events-equipment-loader"
import ProvisionEventsAssetView from "features/provisioning/provisions/provision-events/view/provision-events-asset-view-index"
import { ProvisionEventEditor } from "features/provisioning/provisions/provision-events/edit/provision-event-asset-editor"
import StockManagementListView from "features/iot/stock-management/pages/stock-managment/stock-management-list-view"
import StockManagementLoader from "features/iot/stock-management/loaders/stock-management-loader"
import OnHandSensorsListView from "features/iot/stock-management/pages/stock-managment/onhand-sensors-page/onhand-sensors-list-view"
import StockAllocationListView from "features/iot/stock-management/pages/stock-allocation/stock-allocation-list-view"
import DispatchView from "features/iot/stock-management/pages/dispatch/dispatch-view"
import AllocatedSensorsListView from "features/iot/stock-management/pages/stock-allocation/allocated-sensors-page/allocated-sensors-list-view"
import DispatchHistortView from "features/iot/stock-management/pages/dispatch-history/dispatch-history-view"
import DispatchedSensorsListView from "features/iot/stock-management/pages/dispatch-history/dispatched-sensors-page/dispatched-sensors-list-view"
import CustomerView from "features/organizations/customers/pages/customer/customer-view"
import CustomersListLoader from "features/organizations/customers/pages/customers-list/customers-list-loader"
import CustomerListView from "features/organizations/customers/pages/customers-list/customers-list-view"
import CustomersLoader from "features/iot/stock-management/loaders/customers-loader"

const assetPath = ":customerId/sites/:siteId/assets/:assetId"
const assetSectionPath = assetPath + "/assetsections/:assetSectionId"
const assetSectionEditPath = assetSectionPath + "/editor"

export const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path='/' element={<MainLayout />}>
            <Route element={<CustomersListLoader />}>
                <Route index element={<Navigate to='customers' />} />
                <Route path='customers' element={<CustomerSidebarView />}>
                    <Route path='' element={<CustomerListView />} />
                    <Route path=':customerId' element={<CustomerView />} />
                    <Route path=':customerId/sites/:siteId' element={<SiteView />} />
                    <Route path={assetPath} element={<AssetView />} />
                    <Route path={assetSectionPath} element={<AssetSectionView />} />
                    <Route path={assetSectionEditPath} element={<AssetSectionEditor />} />
                </Route>
            </Route>
            <Route path='sensor-batches' element={<SensorBatchLoader />}>
                <Route path='' element={<SensorBatchListView />} />
                <Route path=':sensorBatchId' element={<SensorBatchView />} />
                <Route path='stock-management' element={<StockManagementListView />} />
            </Route>
            <Route path='stock-management' element={<StockManagementLoader />}>
                <Route path='' element={<StockManagementListView />} />
                <Route element={<CustomersLoader />}>
                    <Route path='onhand-sensors/:partNumber' element={<OnHandSensorsListView />} />
                    <Route path='stock-allocation/:partNumber?' element={<StockAllocationListView />} />
                    <Route path='dispatch/:partNumber?/:customerName?/:siteName?' element={<DispatchView />} />
                    <Route path='allocated/:partNumber?/:customerName?/:siteName?' element={<AllocatedSensorsListView />} />
                    <Route path='dispatch-history/:partNumber?' element={<DispatchHistortView />} />
                    <Route path='dispatched-sensors/:partNumber?/:customerName?/:siteName?' element={<DispatchedSensorsListView />} />
                </Route>
            </Route>

            <Route
                path='provision-events'
                element={<ProvisionEventsCustomerSidebarView />}
                loader={customerRouteDataLoader}
                shouldRevalidate={() => false}
            >
                <Route element={<ProvisionEventsLoader />} loader={provisionEventsRouteDataLoader} shouldRevalidate={() => false}>
                    <Route index path='customer?/:customerId?/site?/:siteId?' element={<ProvisionEventsListView />} />
                    <Route
                        element={<ProvisionEventsEquipmentLoader />}
                        loader={provisionEventEquipmentDataLoader}
                        shouldRevalidate={x => x.currentParams.assetId != x.nextParams.assetId}
                    >
                        <Route
                            path='customer/:customerId/site/:siteId/asset/:assetId/assetsection?/:assetSectionId?/event?/:provisionEventId?'
                            element={<ProvisionEventsAssetView />}
                        />
                        <Route
                            path='customer/:customerId/site/:siteId/asset/:assetId/assetsection/:assetSectionId/event/:provisionEventId/edit'
                            element={<ProvisionEventEditor />}
                        />
                    </Route>
                </Route>
            </Route>
            <Route path='liner-material-library' element={<LinerMaterialLibraryLoader />}>
                <Route index element={<LinerMaterialLibraryListView />} />
            </Route>

            <Route path='/users-management' element={<UsersLoader />}>
                <Route index element={<UsersPage />} />
                <Route path=':userId' element={<UserLoader />}>
                    <Route index element={<UserPage />} />
                </Route>
            </Route>
            <Route path='*' element={<FormattedMessage id='misc.pageNotFound' />} />
        </Route>,
    ),
)
